import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/cms.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "CARMA Heritage & Leadership Proof | Avani Cimcon",
  description:
    "CARMA is a legacy proof page in the Avani Cimcon product archive. It reflects leadership depth, governance experience and the company's broader enterprise software heritage.",
  keywords:
    "CARMA, enterprise software heritage, leadership, management, product archive, technology strategist, software company India, enterprise delivery, product ownership, enterprise engineering",
  canonical: "https://www.avanicimcon.com/carma",
  hero: {
    eyebrow: "Product Heritage",
    heading: "CARMA",
    lead:
      "CARMA remains in the archive as proof that Avani Cimcon has long operated with product ownership, leadership depth and enterprise delivery discipline.",
    image: BannerImg,
    tags: ["Leadership proof", "Product archive", "Enterprise governance", "Long-term delivery"],
    panelKicker: "A heritage page with real organizational context.",
    panelTitle: "A legacy archive entry tied to the people and governance behind the portfolio.",
    panelText:
      "The page is intentionally proof-oriented: it preserves historical evidence of leadership depth and the ability to manage complex software initiatives over time.",
    stats: [
      { value: "1990s+", label: "Long enterprise heritage" },
      { value: "Leadership", label: "Management and strategy depth" },
      { value: "Archive", label: "Preserved product evidence" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See More Products", href: "/more-products" },
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A heritage entry that preserves the company’s leadership and product ownership story",
    lead:
      "CARMA is not presented as an active commercial product. It exists as an archive page that helps establish long-term engineering and governance credibility.",
    paragraphs: [
      "The legacy page has always reflected the fact that Avani Cimcon is promoted and managed by experienced technocrats who have handled international relationships, product strategy and enterprise delivery.",
      "That context is valuable because it shows the company has not only written software, but also owned the decisions, governance and leadership required to keep enterprise work moving over time.",
    ],
    callout: {
      title: "What the page should prove",
      text: "This archive entry demonstrates the people and management depth behind the product portfolio.",
      items: [
        "Leadership and governance",
        "International delivery exposure",
        "Enterprise software strategy",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "The page preserves evidence of management depth behind enterprise software delivery",
    lead:
      "The value here is organizational proof, not a product pitch.",
    items: [
      {
        title: "Ownership and strategy",
        text: "The company needed leaders who could guide software programs, customer relationships and product direction.",
      },
      {
        title: "International coordination",
        text: "The leadership story includes long-standing exposure to international relationships and delivery.",
      },
      {
        title: "Enterprise credibility",
        text: "The page helps show that product work was grounded in experienced management, not ad hoc experimentation.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "The architecture story here is organizational: governance, judgment and long-term stewardship",
    lead:
      "This is an archive page, so the architecture emphasis is on how enterprise work is led and maintained over time.",
    paragraphs: [
      "The original page content centered on the Managing Director’s background in technology strategy, agile management and entrepreneurial innovation, which makes it a useful proof page for enterprise governance.",
      "It shows the organizational architecture around products: chief architect vision, customer-facing leadership and the ability to frame business and information system strategy.",
    ],
    callout: {
      title: "Leadership highlights",
      items: [
        "Technology strategist perspective",
        "Chief architect responsibility",
        "Business and information system framing",
        "Customer-facing leadership experience",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "CARMA helps demonstrate the company’s broader enterprise software capability",
    lead:
      "Even as a heritage page, it still contributes to the product archive and company credibility.",
    items: [
      {
        title: "Product ownership",
        text: "The archive shows the company has owned software ideas long enough to preserve them as part of the site history.",
      },
      {
        title: "Enterprise management",
        text: "The page reflects leadership experience in managing international relationships and strategic software direction.",
      },
      {
        title: "Architectural judgment",
        text: "It reinforces that the company operates with a chief-architect mindset.",
      },
      {
        title: "Delivery maturity",
        text: "The page is proof that the business has handled long-term enterprise commitments.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "The archive preserves the challenge of leading software work across markets and relationships",
    lead:
      "That leadership context is part of the product heritage story.",
    items: [
      {
        title: "Cross-border relationships",
        text: "Managing international relationships requires more than code; it requires trust and continuity.",
      },
      {
        title: "Strategic direction",
        text: "Software programs need clear technical and business guidance to remain relevant.",
      },
      {
        title: "Long-term stewardship",
        text: "Product archives matter because they show what the company chose to keep and why.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The page scales as evidence, not as a commercial offer",
    lead:
      "That makes it useful in the product heritage cluster without turning it into a brochure.",
    paragraphs: [
      "Keeping CARMA in the archive ensures the site retains a tangible reminder of leadership depth, product stewardship and the broader management context behind the company.",
      "The page scales as proof because it links to the rest of the enterprise story rather than trying to become a self-contained sales page.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Leadership is part of product credibility",
        "Archive pages can preserve strategic proof",
        "Product heritage supports enterprise trust",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "The CARMA archive helps explain why clients can trust the company with long-term programs",
    lead:
      "The benefit is not the product itself. It is the evidence of stable leadership and product stewardship.",
    items: [
      {
        title: "Governance confidence",
        text: "Clients see that the organization has experienced leadership behind its delivery.",
      },
      {
        title: "Long-term stability",
        text: "The archive suggests continuity and long-horizon thinking.",
      },
      {
        title: "Product stewardship",
        text: "The company preserves its own history instead of erasing it.",
      },
      {
        title: "Enterprise credibility",
        text: "Leadership and architecture depth improve trust in modernization work.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Archive pages can still strengthen the modernization narrative",
    lead:
      "CARMA supports the broader story that Avani Cimcon has matured through years of enterprise delivery.",
    paragraphs: [
      "This page can remain a compact heritage proof page while still linking into modernization and AI narratives elsewhere on the site.",
      "That is important because enterprise buyers often want to understand who has been behind the software over time, not just what the software did.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Keep the archive intact",
        "Preserve leadership proof",
        "Connect to company and services pages",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI is secondary here and should remain a supporting theme",
    lead:
      "The page is about heritage and governance, so AI should only appear as a modern extension of enterprise leadership.",
    items: [
      {
        title: "Strategic analysis",
        text: "Use AI to help analyze product and governance history across the archive.",
      },
      {
        title: "Knowledge access",
        text: "Use AI to make legacy experience easier to find and reuse internally.",
      },
      {
        title: "Decision support",
        text: "Use AI to support leadership and modernization planning, not to replace it.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "The leadership story supports the company’s work across all the industries it serves",
    lead:
      "These links keep the archive connected to the broader enterprise narrative.",
    items: [
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Travel domain evidence." },
      { label: "Insurance", href: "/insurance", text: "Regulated workflow proof." },
      { label: "Healthcare", href: "/healthcare", text: "Operational complexity and trust." },
      { label: "Legal", href: "/legal", text: "Workflow and document rigor." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "CARMA connects to the company’s broader proof and delivery stack",
    lead:
      "The archive is more meaningful when linked to the rest of the site’s authority pages.",
    items: [
      { label: "About", href: "/about", text: "Company history and credibility." },
      { label: "Management", href: "/management", text: "Leadership and governance." },
      { label: "Expertise & Experience", href: "/expertise-experience", text: "Delivery depth and technical breadth." },
      { label: "Value Proposition", href: "/value-proposition", text: "Enterprise trust and commitment." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "CARMA preserves the leadership context that makes the company credible",
    lead:
      "That context matters for enterprise buyers who want to know who is behind the work.",
    items: [
      {
        title: "Management depth",
        text: "The page records strategic and technical leadership experience.",
      },
      {
        title: "Archive integrity",
        text: "The company keeps legacy evidence available instead of hiding it.",
      },
      {
        title: "Enterprise trust",
        text: "Long-term leadership is part of why the brand is credible.",
      },
      {
        title: "Proof of stewardship",
        text: "CARMA is a reminder that Avani Cimcon has been managing software and relationships for years.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Use the leadership behind CARMA to support your own enterprise program",
    text:
      "If you need a long-term partner with architectural judgment and management depth, we should talk.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class Carma extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default Carma;
